import { gql, useQuery } from '@apollo/client';
import type { Notice } from '@mnd-frontend/graphql-schema';
import { toBoolean } from '@mnd-frontend/ts';
import { useToasts } from '@mnd-frontend/ui/publish/contexts';
import { camelCase, difference, differenceWith, isEqual, uniq, upperFirst } from 'lodash-es';
import type { FC } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation } from 'react-router';
import { useCurrentPressroom } from './CurrentPressroomContext';

type NoticeName<T extends Notice> = `show${Capitalize<T>}Notice`;
type NoticeToRender<T extends Notice> = Record<NoticeName<T>, boolean>;
type NoticeWithOptions<T extends Notice> = [
  notice: T,
  options: { blockMatchingPatterns?: string[] },
];
type NoticeWithOptionalOptions<T extends Notice> = T | NoticeWithOptions<T>;

const NoticesContext = createContext<{
  activeNotices: NoticeWithOptions<Notice>[];
  noticeToRender: Partial<NoticeToRender<Notice>>;
  addNotices: (noticeWithOptions: NoticeWithOptions<Notice>[]) => void;
  removeNotices: (noticeWithOptions: NoticeWithOptions<Notice>[]) => void;
  anyNoticeActive: boolean;
}>({
  activeNotices: [],
  noticeToRender: {},
  addNotices: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  removeNotices: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  anyNoticeActive: false,
});

const toNoticeWithOptions = (
  noticeWithOptionalOptions: NoticeWithOptionalOptions<Notice>[],
): NoticeWithOptions<Notice>[] =>
  noticeWithOptionalOptions.map(noticeWithOptionalOption => {
    const notice = Array.isArray(noticeWithOptionalOption)
      ? noticeWithOptionalOption[0]
      : noticeWithOptionalOption;
    const blockMatchingPatterns = Array.isArray(noticeWithOptionalOption)
      ? [noticeWithOptionalOption[1]?.blockMatchingPatterns].flat().filter(toBoolean)
      : [];

    return [notice, { blockMatchingPatterns }];
  });

const mergeNoticesWithOptions = (
  currentNotices: NoticeWithOptions<Notice>[],
  newNotices: NoticeWithOptions<Notice>[],
) => {
  let dirty = false;
  const newNoticesWithOptions = [...currentNotices];
  const missingNotices = differenceWith(newNotices, newNoticesWithOptions, isEqual);

  missingNotices.forEach(([missingNotice, missingNoticeOptions]) => {
    const currentNotice = newNoticesWithOptions.find(
      newNoticesWithOption => newNoticesWithOption[0] === missingNotice,
    );

    if (currentNotice) {
      if (
        missingNoticeOptions.blockMatchingPatterns &&
        difference(
          missingNoticeOptions.blockMatchingPatterns,
          currentNotice[1].blockMatchingPatterns ?? [],
        ).length
      ) {
        dirty = true;
        currentNotice[1].blockMatchingPatterns = uniq([
          ...(currentNotice[1].blockMatchingPatterns ?? []),
          ...missingNoticeOptions.blockMatchingPatterns,
        ]);
      }
    } else {
      dirty = true;
      newNoticesWithOptions.push([missingNotice, missingNoticeOptions]);
    }
  });

  return {
    dirty,
    newNoticesWithOptions,
  };
};

export const NoticesContextProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { showFailureToast } = useToasts();
  const { pressroomId } = useCurrentPressroom();

  const currentBlockMatchingPatterns = useRef<string[]>([]);
  const [activeNotices, setActiveNotices] = useState<NoticeWithOptions<Notice>[]>([]);

  const matchBlockingPatterns = useMemo(
    () =>
      currentBlockMatchingPatterns.current.some(currentBlockMatchingPattern =>
        Boolean(matchPath(currentBlockMatchingPattern, location.pathname)),
      ),
    [location.pathname],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: ?
  useEffect(() => {
    if (!matchBlockingPatterns) {
      currentBlockMatchingPatterns.current = [];
    }
  }, [location.pathname, matchBlockingPatterns]);

  /* eslint-disable graphql/no-deprecated-fields, graphql/required-fields, graphql/template-strings */
  const { data } = useQuery<{
    pressroom: {
      id: string;
      pendingNotices:
        | ({
            id: string;
          } & Record<Notice, boolean>)
        | null;
    } | null;
  }>(
    gql`
      query Notices($pressroomId: ID!) {
        pressroom(id: $pressroomId) {
          id
          pendingNotices {
            id
            ${activeNotices
              .map(noticeWithOptions =>
                camelCase(
                  Array.isArray(noticeWithOptions) ? noticeWithOptions[0] : noticeWithOptions,
                ),
              )
              .join(' ')}
          }
        }
      }
    `,
    {
      skip: activeNotices.length === 0,
      onError: () => showFailureToast(t('common_general_error')),
      variables: {
        pressroomId,
      },
    },
  );
  /* eslint-enable graphql/no-deprecated-fields, graphql/required-fields, graphql/template-strings */

  const addNotices = useCallback(
    (noticeWithOptions: NoticeWithOptions<Notice>[]) =>
      setActiveNotices(
        _activeNotices =>
          mergeNoticesWithOptions(_activeNotices, noticeWithOptions).newNoticesWithOptions,
      ),
    [],
  );
  const removeNotices = useCallback(
    (noticeWithOptions: NoticeWithOptions<Notice>[]) =>
      setActiveNotices(_activeNotices =>
        differenceWith(
          _activeNotices,
          noticeWithOptions,
          (activeNotice, noticeWithOption) => activeNotice[0] === noticeWithOption[0],
        ),
      ),
    [],
  );
  const value = useMemo(() => {
    const noticeToRender: Partial<NoticeToRender<Notice>> = {};
    let first = true;

    activeNotices.forEach(([notice, options]) => {
      const gqlFormatedNotice = camelCase(notice) as Notice;
      const showFormatedNotice = `show${upperFirst(notice)}Notice` as NoticeName<Notice>;
      const activeNoticeValue = Boolean(data?.pressroom?.pendingNotices?.[gqlFormatedNotice]);

      if (!matchBlockingPatterns && first && activeNoticeValue && !window.Cypress) {
        first = false;
        noticeToRender[showFormatedNotice] = true;

        currentBlockMatchingPatterns.current = options?.blockMatchingPatterns ?? [];
      } else {
        noticeToRender[showFormatedNotice] = false;
      }
    });

    return {
      activeNotices,
      noticeToRender,
      anyNoticeActive: !first,
      addNotices,
      removeNotices,
    };
  }, [
    activeNotices,
    addNotices,
    data?.pressroom?.pendingNotices,
    matchBlockingPatterns,
    removeNotices,
  ]);

  return <NoticesContext.Provider value={value}>{children}</NoticesContext.Provider>;
};

export const useIsAnyNoticeActive = () => useContext(NoticesContext).anyNoticeActive;

export const useNotices = <T extends Notice>(
  noticesWithOptionalOptions: NoticeWithOptionalOptions<T>[],
  options?: { skip?: boolean },
) => {
  const removeIdRef = useRef<NodeJS.Timeout>();
  const { activeNotices, noticeToRender, addNotices, removeNotices } = useContext(NoticesContext);

  const noticesWithOptions = useMemo(
    () => toNoticeWithOptions(noticesWithOptionalOptions),
    [noticesWithOptionalOptions],
  );

  const missingNoticesWithOptionalOptions = useMemo(
    () => mergeNoticesWithOptions(activeNotices, noticesWithOptions).dirty,
    [activeNotices, noticesWithOptions],
  );

  useEffect(() => {
    if (options?.skip) {
      return;
    }

    if (removeIdRef.current) {
      clearTimeout(removeIdRef.current);
      removeIdRef.current = undefined;
    }

    if (missingNoticesWithOptionalOptions) {
      addNotices(noticesWithOptions);
    }

    return () => {
      removeIdRef.current = setTimeout(() => {
        removeNotices(noticesWithOptions);
      }, 50);
    };
  }, [
    addNotices,
    missingNoticesWithOptionalOptions,
    noticesWithOptions,
    options?.skip,
    removeNotices,
  ]);

  return noticeToRender as NoticeToRender<T>;
};
